import { Injectable } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';
import {
  AnalyticsService,
  ProgramService,
  SeoService,
  TRACKED_EVENTS,
} from '@univacity/angular/datas';
import {
  ApiError,
  FindManyProgram,
  Option,
  PaginateResult,
  Program,
} from '@univacity/shared/types';
import {
  EMPTY,
  Observable,
  debounceTime,
  distinctUntilChanged,
  map,
  of,
  switchMap,
  tap,
} from 'rxjs';

export interface ProgramState {
  programs: Program[];
  loading?: boolean;
  error?: ApiError;
  hasNextPage?: boolean;
  hasPrevPage?: boolean;
  limit?: number;
  offset?: number;
  page?: number;
  pagingCounter?: number;
  totalDocs: number;
  totalPages?: number;
  prevPage?: number | null;
  nextPage?: number | null;
  query?: FindManyProgram;
  similarSearch?: string[];
}

const initialState: ProgramState = {
  programs: [],
  totalDocs: 0,
};

@Injectable()
export class ProgramsStore extends ComponentStore<ProgramState> {
  query?: Params;
  readonly programs$: Observable<Program[]> = this.select(
    (state) => state.programs
  );
  readonly totalDocs$: Observable<number | undefined> = this.select(
    (state) => state.totalDocs
  );
  readonly loading$: Observable<boolean | undefined> = this.select(
    (state) => state.loading
  );
  readonly query$: Observable<FindManyProgram | undefined> = this.select(
    (state) => state.query
  );
  readonly similarSearch$: Observable<string[]> = this.select(
    (state) => state.similarSearch || []
  );
  readonly queryArray$: Observable<Array<{ key: string; value: string[] }>> =
    this.select(
      this.query$,
      (query: any) =>
        (query &&
          Object.keys(query)
            .filter((k) => !['page', 'limit', 'sort', 'currency'].includes(k))
            .map((key) => ({
              key,
              value: Array.isArray(query[key]) ? query[key] : [query[key]],
            }))) ||
        []
    );

  constructor(
    private programService: ProgramService,
    private route: ActivatedRoute,
    private seo: SeoService,
    private analytics: AnalyticsService
  ) {
    super(initialState);
    this.fetchPrograms(this.fetchProgramsData$);
  }

  private readonly fetchProgramsData$ = this.select(
    {
      query: this.route.queryParams.pipe(debounceTime(800)),
    },
    { debounce: true } // 👈 setting this selector to debounce
  );
  private readonly fetchSimmilarProgramData$ = this.select(
    {
      query: this.route.queryParams.pipe(
        debounceTime(800),
        map(({ page, sort, limit, ...q }) => JSON.stringify(q)),
        distinctUntilChanged(),
        map((v) => JSON.parse(v))
      ),
    },
    { debounce: true } // 👈 setting this selector to debounce
  );

  private readonly fetchPrograms = this.effect(
    (
      fetchProgramsData$: Observable<{
        query: Params;
      }>
    ) => {
      return fetchProgramsData$.pipe(
        switchMap(({ query }) => {
          this.setState((state) => ({ ...state, loading: true }));
          this.analytics.track(TRACKED_EVENTS.programsSearch, query);

          return this.programService
            .findMinimal({ ...query, ...(this.query || {}) })
            .pipe(
              tap((res) =>
                this.seo.updateSearchPageLink(res.nextPage, res.prevPage)
              ),
              tapResponse(
                (res) => this.updateProgramsResults({ res, query }),
                () => this.setState((state) => ({ ...state, loading: true }))
              )
            );
        })
      );
    }
  );
  private readonly fetchProgramsSimilarSearch = this.effect(
    (
      fetchProgramsData$: Observable<{
        query: Params;
      }>
    ) => {
      let attepmt = 1;
      return fetchProgramsData$.pipe(
        switchMap(({ query }) => {
          return this.programService
            .autoComplete(query['search'], 1, query)
            .pipe(
              tap((res) => {
                if (res.options.length === 0 && attepmt++ <= 1) {
                  this.fetchProgramsSimilarSearch(of({ query: {} }));
                }
              }),
              tapResponse(
                (res) => this.updateProgramsSuggestionResults(res),
                () => EMPTY
              )
            );
        })
      );
    }
  );

  private updateProgramsResults = this.updater(
    (
      state,
      {
        res: { docs, ...meta },
        query,
      }: { res: PaginateResult<Program>; query: Params }
    ) => ({
      ...state,
      ...meta,
      programs: docs,
      query,
      loading: false,
    })
  );

  private updateProgramsSuggestionResults = this.updater(
    (state, { options }: { options: Option[] }) => ({
      ...state,
      similarSearch: [...new Set(options.map((o) => o.label))],
    })
  );

  initFetchFiltersAndSuggestions() {
    this.fetchProgramsSimilarSearch(this.fetchSimmilarProgramData$);
  }

  selectFromState(prop: string): Observable<any> {
    return this.select((state) => ({ ...state }[prop]));
  }

  delete(id: string) {
    this.programService.deleteProgram(id).subscribe(
      this.updater((state) => {
        return {
          ...state,
          programs: state.programs.filter((p) => p._id !== id),
          totalDocs: state.totalDocs - 1,
        };
      })
    );
  }
}
